import { useEffect, useRef } from 'react';
import { getSample } from '../audio/registry';
import type { SourceMeta } from '../types';

interface Props {
  source: SourceMeta;
  height?: number;
  /** Slice index under the cursor / selected, highlighted. */
  activeSlice?: number;
  /** Slice currently sounding, or -1. Drawn brighter than a hover. */
  playSlice?: number;
  /** How far through that slice, 0..1. Reversed steps run backwards. */
  playFraction?: number;
  showTransients?: boolean;
  onSliceClick?: (index: number, e: React.MouseEvent) => void;
  onScrub?: (seconds: number) => void;
}

/**
 * Loop waveform with the chop grid drawn over it. Fits the whole file to the
 * panel width — these are loops, not arrangements, so scrolling would only get
 * in the way.
 */
export function Waveform({
  source,
  height = 150,
  activeSlice = -1,
  playSlice = -1,
  playFraction = 0,
  showTransients = true,
  onSliceClick,
  onScrub,
}: Props) {
  const ref = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const entry = getSample(source.id);
    const dpr = window.devicePixelRatio || 1;
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;
    canvas.width = w * dpr;
    canvas.height = h * dpr;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, w, h);

    ctx.fillStyle = '#0b0e11';
    ctx.fillRect(0, 0, w, h);

    const dur = source.duration || 1;
    const xOf = (t: number) => (t / dur) * w;

    // Alternating slice bands make it obvious where the chops land.
    const edges = source.sliceEdges;
    for (let i = 0; i + 1 < edges.length; i++) {
      const x0 = xOf(edges[i]);
      const x1 = xOf(edges[i + 1]);
      if (i === playSlice) {
        ctx.fillStyle = `${source.color}4d`;
      } else if (i === activeSlice) {
        ctx.fillStyle = `${source.color}33`;
      } else {
        ctx.fillStyle = i % 2 === 0 ? '#0f1418' : '#0b0e11';
      }
      ctx.fillRect(x0, 0, Math.max(1, x1 - x0), h);
    }

    // Waveform
    if (entry) {
      const peaks = entry.peaks;
      const buckets = peaks.length / 2;
      const mid = h / 2;
      ctx.fillStyle = '#7f8c99';
      for (let x = 0; x < w; x++) {
        const b0 = Math.floor((x / w) * buckets);
        const b1 = Math.max(b0 + 1, Math.floor(((x + 1) / w) * buckets));
        let lo = 0;
        let hi = 0;
        for (let b = b0; b < b1 && b < buckets; b++) {
          lo = Math.min(lo, peaks[b * 2]);
          hi = Math.max(hi, peaks[b * 2 + 1]);
        }
        const y0 = mid - hi * mid * 0.92;
        const y1 = mid - lo * mid * 0.92;
        ctx.fillRect(x, y0, 1, Math.max(1, y1 - y0));
      }
    } else {
      ctx.fillStyle = '#3c4753';
      ctx.font = '12px "Fredoka", sans-serif';
      ctx.fillText('audio not loaded', 12, h / 2);
    }

    if (showTransients) {
      ctx.fillStyle = 'rgba(76, 201, 240, 0.35)';
      for (const t of source.transients) ctx.fillRect(xOf(t), h - 10, 1, 10);
    }

    // Slice edges — beat lines brighter than subdivisions.
    for (let i = 0; i < edges.length; i++) {
      const x = xOf(edges[i]);
      const isBeat = i % source.division === 0;
      ctx.fillStyle = isBeat ? `${source.color}cc` : 'rgba(230,235,239,0.18)';
      ctx.fillRect(x, 0, isBeat ? 1.5 : 1, h);
      if (isBeat && i + 1 < edges.length) {
        ctx.fillStyle = 'rgba(230,235,239,0.45)';
        ctx.font = '9px "DM Mono", monospace';
        ctx.fillText(String(i), x + 3, 11);
      }
    }

    // Downbeat / offset marker
    ctx.fillStyle = '#fbbf24';
    ctx.fillRect(xOf(source.offset) - 1, 0, 2, h);

    /*
     * Sample playhead. A pattern jumps around the loop rather than running
     * through it, so this is "where in the source the sound you're hearing
     * comes from" — it teleports between slices, which is the point.
     */
    if (playSlice >= 0 && playSlice + 1 < edges.length) {
      const from = edges[playSlice];
      const to = edges[playSlice + 1];
      const x = xOf(from + Math.max(0, Math.min(1, playFraction)) * (to - from));
      ctx.fillStyle = `${source.color}22`;
      ctx.fillRect(xOf(from), 0, Math.max(1, xOf(to) - xOf(from)), h);
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(x - 1, 0, 2, h);
      ctx.fillStyle = source.color;
      ctx.fillRect(x - 3, 0, 6, 3);
      ctx.fillRect(x - 3, h - 3, 6, 3);
    }
  }, [source, activeSlice, playSlice, playFraction, showTransients]);

  const sliceAt = (clientX: number, el: HTMLCanvasElement): number => {
    const rect = el.getBoundingClientRect();
    const t = ((clientX - rect.left) / rect.width) * source.duration;
    for (let i = 0; i + 1 < source.sliceEdges.length; i++) {
      if (t >= source.sliceEdges[i] && t < source.sliceEdges[i + 1]) return i;
    }
    return -1;
  };

  return (
    <canvas
      ref={ref}
      style={{ height }}
      className="w-full cursor-pointer rounded-lg border border-ink-700"
      onClick={(e) => {
        const idx = sliceAt(e.clientX, e.currentTarget);
        const rect = e.currentTarget.getBoundingClientRect();
        onScrub?.(((e.clientX - rect.left) / rect.width) * source.duration);
        if (idx >= 0) onSliceClick?.(idx, e);
      }}
    />
  );
}
